import styled from "@emotion/styled";
import type { FC, KeyboardEvent } from "react";
import { useBloc } from "@blac/react";
import Path from "src/constants/path";
import translate from "src/lib/translate";
import { dateLocalString } from "src/lib/date";
import {
  AppQueryPopupsController,
  AppPopup
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import { IconAlertCircle, IconCheckCircle } from "src/constants/icons";
import RefillRequirementsCubit from "src/state/RefillRequirementsCubit/RefillRequirementsCubit";
import type {
  RefillCardVariant,
  RefillRequirementsResponse
} from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";
import { getRefillCohort } from "src/lib/refillCohort";
import { RefillColors } from "src/constants/refillColors";

const HOME_RETURN_URL = `/${Path.app}/${Path.home}`;

type VisibleVariant = Exclude<RefillCardVariant, "hidden">;

// Per-variant treatment (Figma: Medication Experience – v1, "Cards variants" 5978:69955).
// windowOpen → PurpleHaze, actionRequired → Accessible Red, secured → Accessible Green;
// each is a translucent tint of its accent over white, with a solid accent border.
const VARIANT_STYLES: Record<
  VisibleVariant,
  { background: string; border: string; color: string }
> = {
  windowOpen: {
    background: "rgba(95, 31, 143, 0.12)",
    border: RefillColors.purpleHaze,
    color: RefillColors.accessiblePurpleHaze
  },
  overdue: {
    background: "rgba(255, 189, 112, 0.2)",
    border: RefillColors.sunriseOrange,
    color: RefillColors.accessibleOrange
  },
  actionRequired: {
    background: "rgba(255, 212, 200, 0.54)",
    border: RefillColors.accessibleRed,
    color: RefillColors.accessibleRed
  },
  secured: {
    background: "rgba(195, 245, 215, 0.64)",
    border: RefillColors.inRange,
    color: RefillColors.accessibleGreen
  }
};

const Card = styled("div", {
  shouldForwardProp: (p) => p !== "$variant"
})<{ $variant: VisibleVariant }>`
  position: relative;
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  background: ${({ $variant }) => VARIANT_STYLES[$variant].background};
  border: 1px solid ${({ $variant }) => VARIANT_STYLES[$variant].border};
  color: ${({ $variant }) => VARIANT_STYLES[$variant].color};
`;

const IconWrap = styled.div`
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.span`
  font-weight: 500;
  /* 16px per Figma (Info Cards title); the block otherwise inherits as-little's 14px. */
  font-size: 1rem;
`;

const Body = styled.span`
  font-weight: 400;
`;

const SeeRequirements = styled.span`
  font-weight: 500;
  /* 12px below the subtitle per Figma (mb-12): 4px TextBlock gap + 8px here. */
  margin-top: 0.5rem;
`;

const formatDeadline = (isoDate?: string | null): string =>
  isoDate
    ? dateLocalString(isoDate, {
        weekday: "short",
        month: "long",
        day: "numeric"
      })
    : "";

const getCopy = (
  variant: VisibleVariant,
  data?: RefillRequirementsResponse
): { title: string; body: string; cta: string } => {
  switch (variant) {
    case "secured":
      return {
        title: translate("refillCard.secured.title"),
        body: translate("refillCard.secured.description", {
          date: formatDeadline(data?.nextExpiresAt)
        }),
        cta: translate("refillCard.secured.cta")
      };
    case "actionRequired":
      return {
        title: translate("refillCard.actionNeeded.title"),
        body: translate("refillCard.actionNeeded.description"),
        cta: translate("refillCard.cta")
      };
    case "overdue":
      return {
        title: data?.medicationName
          ? translate("refillCard.overdue.title", {
              medication: data.medicationName
            })
          : translate("refillCard.overdue.titleGeneric"),
        // No date here, unlike `windowOpen`: the deadline has passed, so printing it would read
        // "meet your requirements by" a day already gone (FEWEB-2710).
        body: translate("refillCard.overdue.description"),
        cta: translate("refillCard.cta")
      };
    case "windowOpen":
    default:
      return {
        title: data?.medicationName
          ? translate("refillCard.title", { medication: data.medicationName })
          : translate("refillCard.titleGeneric"),
        body: translate("refillCard.deadline", {
          date: formatDeadline(data?.dueAt)
        }),
        cta: translate("refillCard.cta")
      };
  }
};

/**
 * Home-screen entry point to the refill hub (FEWEB-2502). Self-hiding and
 * non-dismissable. Cohort-first: concierge "Yes" → purple `windowOpen` (before the target
 * date) or orange `overdue` (after, and for as long as the refill stays unfinished); concierge
 * "No" → green `secured` / red `actionRequired` (see {@link VARIANT_STYLES} and
 * `getRefillCardVariant`).
 */
const RefillCard: FC = () => {
  const [{ data }, { getCardVariant }] = useBloc(RefillRequirementsCubit);

  const cohort = getRefillCohort();
  const variant = getCardVariant(cohort);

  if (variant === "hidden") {
    return null;
  }

  const { title, body, cta } = getCopy(variant, data);

  const openHub = () => {
    AppQueryPopupsController.openPopup(AppPopup.prepareRefill, {
      additionalParameters: { returnUrl: HOME_RETURN_URL }
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openHub();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      data-variant={variant}
      $variant={variant}
      onClick={openHub}
      onKeyDown={handleKeyDown}
    >
      <IconWrap aria-hidden="true">
        {variant === "secured" ? <IconCheckCircle /> : <IconAlertCircle />}
      </IconWrap>
      <TextBlock className="as-little">
        <Title>{title}</Title>
        <Body>{body}</Body>
        <SeeRequirements>{cta}</SeeRequirements>
      </TextBlock>
    </Card>
  );
};

export default RefillCard;
